import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { Button } from "./Button";

interface SignaturePadProps {
  /**
   * Called whenever the stroke state changes. `hasStroke` flips true on the
   * first drawn point and back to false on Clear, so the parent can gate its
   * Submit button. `toPngBase64` returns the canvas as a base64 PNG without
   * the `data:image/png;base64,` prefix. The backend strips a prefix too, but
   * the wire field `signaturePngBase64` carries the bare base64.
   */
  onChange: (state: { hasStroke: boolean; toPngBase64: () => string }) => void;
  label: string;
  clearLabel: string;
  ariaLabel: string;
}

/**
 * Signature canvas for mouse, touch and stylus, with no third-party dependency.
 *
 * Pointer Events give one code path for all three. The backing canvas is
 * sized to its CSS box times `devicePixelRatio`, so strokes stay sharp on
 * high-density screens. Export is `canvas.toDataURL("image/png")` with the
 * data-URL prefix stripped. The enrollment e-sign ceremony and the member
 * CMS-10882 signing page use it.
 */
export function SignaturePad({
  onChange,
  label,
  clearLabel,
  ariaLabel,
}: SignaturePadProps): ReactNode {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const [hasStroke, setHasStroke] = useState(false);

  // Size the backing store to the CSS box times the DPR once, on mount, and fix
  // the 2d context's stroke style. It never resizes afterwards, because a
  // resize clears the canvas and would wipe a signature in progress.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0f172a";
  }, []);

  // iOS Safari steals slow touch strokes for scroll and gesture handling on a
  // scrollable page, and treats touch listeners as passive by default, so
  // blocking the native gesture needs native listeners registered with
  // `{ passive: false }`. React's `onTouchStart`/`onTouchMove` props don't
  // work, because React attaches them passively at the root. Drawing stays on
  // the Pointer Events path below; these listeners only `preventDefault()`, so
  // WebKit lets the pointer events through.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const blockNativeGesture = (e: TouchEvent): void => {
      e.preventDefault();
    };
    canvas.addEventListener("touchstart", blockNativeGesture, { passive: false });
    canvas.addEventListener("touchmove", blockNativeGesture, { passive: false });
    return () => {
      canvas.removeEventListener("touchstart", blockNativeGesture);
      canvas.removeEventListener("touchmove", blockNativeGesture);
    };
  }, []);

  const emit = (next: boolean): void => {
    onChange({
      hasStroke: next,
      toPngBase64: () => {
        const canvas = canvasRef.current;
        if (!canvas) return "";
        const dataUrl = canvas.toDataURL("image/png");
        const comma = dataUrl.indexOf(",");
        return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
      },
    });
  };

  const pointFromEvent = (e: ReactPointerEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>): void => {
    e.preventDefault();
    const canvas = canvasRef.current;
    // Pointer capture keeps strokes tracking when the finger/mouse leaves the
    // canvas mid-draw. Guard the call: some environments (and jsdom) don't
    // implement it, and browsers throw if the pointer id isn't active.
    try {
      canvas?.setPointerCapture(e.pointerId);
    } catch {
      // best-effort; drawing works without capture
    }
    drawingRef.current = true;
    lastPointRef.current = pointFromEvent(e);
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>): void => {
    if (!drawingRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    const last = lastPointRef.current;
    if (!ctx || !last) return;
    const p = pointFromEvent(e);
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
    if (!hasStroke) {
      setHasStroke(true);
      emit(true);
    }
  };

  const endStroke = (): void => {
    drawingRef.current = false;
    lastPointRef.current = null;
  };

  const clear = (): void => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasStroke(false);
    emit(false);
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={clear}
          disabled={!hasStroke}
          aria-label={clearLabel}
        >
          {clearLabel}
        </Button>
      </div>
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={ariaLabel}
        className="h-48 w-full touch-none select-none rounded-md border-2 border-dashed border-slate-300 bg-white"
        // iOS long-press hijack guard: the text-selection loupe/callout cancels
        // a slow-starting stroke. No Tailwind utility exists for touch-callout.
        style={{ WebkitTouchCallout: "none" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endStroke}
        onPointerLeave={endStroke}
        onPointerCancel={endStroke}
      />
    </div>
  );
}
